import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightLeft,
  ExternalLink,
  LayoutDashboard,
  Mail,
  Receipt,
  Upload,
  Users,
} from 'lucide-react';

interface OperationsTabProps {
  clientId: number;
  companyName: string;
  whiteLabelSlug: string | null;
}

const LINKS = [
  { suffix: '/portal/dashboard', label: 'Dashboard', icon: LayoutDashboard, desc: 'Float, runs summary, partner KPIs' },
  { suffix: '/portal/users', label: 'Registered users', icon: Users, desc: 'Attributed reps — list and CSV/XLSX export' },
  { suffix: '/portal/runs', label: 'Disbursement runs', icon: ArrowRightLeft, desc: 'Create, submit, approve runs' },
  { suffix: '/portal/upload', label: 'Upload beneficiaries', icon: Upload, desc: 'Bulk beneficiary file upload' },
  { suffix: '/portal/team', label: 'Portal team', icon: Users, desc: 'CP portal login users (mirror)' },
  { suffix: '/portal/welcome-email', label: 'Welcome email', icon: Mail, desc: 'Rep welcome email config' },
  { suffix: '/portal/finance', label: 'Finance', icon: Receipt, desc: 'Float statement, NQR share, invoices' },
] as const;

export const OperationsTab: React.FC<OperationsTabProps> = ({
  clientId,
  companyName,
  whiteLabelSlug,
}) => {
  const navigate = useNavigate();
  const base = `/admin/channel-partners/${clientId}`;

  return (
    <div className="space-y-4">
      <div className="mymoolah-card p-5">
        <h3 className="admin-text-heading text-lg">Partner operations (admin mirror)</h3>
        <p className="text-sm text-gray-500 mt-1">
          Open the same views {companyName} sees in their portal — with MM Admin privileges
          (approve runs, welcome email, etc.).
        </p>
        <button
          type="button"
          onClick={() => navigate(`${base}/portal/dashboard`)}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[var(--primary)] px-4 py-2.5 text-sm font-semibold text-[var(--primary-foreground)]"
        >
          <ExternalLink className="h-4 w-4" />
          Open full portal view
        </button>
        {whiteLabelSlug && (
          <p className="mt-3 text-xs text-gray-500">
            Public registration link:{' '}
            <span className="font-mono text-gray-700">/register/{whiteLabelSlug}</span>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {LINKS.map((link) => {
          const Icon = link.icon;
          return (
            <button
              key={link.suffix}
              type="button"
              onClick={() => navigate(`${base}${link.suffix}`)}
              className="mymoolah-card flex items-start gap-3 p-4 text-left transition-colors hover:border-[var(--primary)]/30 hover:bg-[var(--primary)]/[0.02]"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)]">
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-gray-900">{link.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{link.desc}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
